import React, { useEffect, useState } from "react";
import { api, fmtIST } from "../lib/api";
import { useAuth } from "../lib/auth";
import Shell, { PageHeader } from "../components/Shell";
import { Plus, Trash, PencilSimple, X } from "@phosphor-icons/react";

// Online = active within the last 5 minutes (backend refreshes last_seen_at)
const isOnline = (u) => u.last_seen_at && (Date.now() - new Date(u.last_seen_at).getTime()) < 5 * 60 * 1000;

const ROLES = [
  { value: "super_admin", label: "Super Admin", desc: "Full access across all modules." },
  { value: "warehouse_manager", label: "Warehouse Manager", desc: "SKU Master + Dispatches." },
  { value: "store_manager", label: "Store Manager", desc: "All store ops for assigned stores." },
  { value: "cashier", label: "Cashier", desc: "POS billing + invoices at assigned stores." },
  { value: "operator", label: "Store Operator", desc: "Store ops except invoicing. Cannot edit back-dated records." },
];

// Grouped catalog of every assignable permission. Role defaults come from the
// backend (/permissions/catalog); anything can be granted on top of any role.
const PERM_GROUPS = [
  { group: "POS & Invoicing", perms: [
    ["pos.bill", "Create invoices"],
    ["pos.price_override", "Change selling price in POS"],
    ["pos.refund", "Refunds"],
    ["invoices.view", "View invoices"],
    ["invoices.edit", "Edit invoices"],
    ["invoices.cancel", "Cancel invoices"],
  ]},
  { group: "Inventory", perms: [
    ["inventory.view", "View stock"],
    ["inventory.inward", "Inward stock"],
    ["inventory.reco", "Reconciliation"],
    ["inventory.bulk_import", "Bulk CSV import"],
    ["inventory.edit", "Edit item details/stock"],
    ["barcodes.print", "Print barcodes"],
  ]},
  { group: "Warehouse", perms: [
    ["warehouse.view", "View SKU master"],
    ["warehouse.sku.edit", "Edit SKU master"],
    ["warehouse.dispatch", "Create dispatches"],
  ]},
  { group: "Reports & Day Close", perms: [
    ["reports.view", "View reports"],
    ["reports.export", "Export reports"],
    ["day_close.manage", "Day close / Z-report"],
  ]},
  { group: "Cash Register", perms: [
    ["cash.view", "View cash register"],
    ["cash.submit", "Submit cash to company"],
  ]},
  { group: "Customers", perms: [
    ["customers.view", "View customers"],
  ]},
  { group: "Administration", perms: [
    ["stores.manage", "Manage stores"],
    ["users.manage", "Manage users"],
    ["audit.view", "View audit log"],
    ["settings.manage", "Company settings"],
  ]},
];

export default function Users() {
  const { stores } = useAuth();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [roleDefaults, setRoleDefaults] = useState({});

  const load = async () => {
    const r = await api.get("/users");
    setList(r.data);
  };
  useEffect(() => {
    load();
    api.get("/permissions/catalog").then((r) => setRoleDefaults(r.data.role_defaults || {})).catch(() => {});
    const t = setInterval(load, 30000); // keep online status fresh
    return () => clearInterval(t);
  }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/users/${editing.id}`, {
          name: editing.name, role: editing.role, store_ids: editing.store_ids || [],
          permissions: editing.permissions || [], password: editing.password || undefined,
        });
      } else {
        await api.post("/users", {
          email: editing.email, password: editing.password, name: editing.name,
          role: editing.role, store_ids: editing.store_ids || [], permissions: editing.permissions || [],
        });
      }
      setEditing(null); load();
    } catch (e) { alert("Failed: " + (e.response?.data?.detail || e.message)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    await api.delete(`/users/${u.id}`);
    load();
  };

  const togglePerm = (p, viaRole) => {
    const arr = new Set(editing.permissions || []);
    if (viaRole) {
      // Role-default perm → toggle an explicit revoke ("-perm")
      if (arr.has("-" + p)) arr.delete("-" + p); else arr.add("-" + p);
    } else {
      if (arr.has(p)) arr.delete(p); else arr.add(p);
    }
    setEditing({ ...editing, permissions: [...arr] });
  };
  const toggleGroup = (perms, allOn) => {
    const arr = new Set(editing.permissions || []);
    const inRole = new Set(roleDefaults[editing.role] || []);
    perms.forEach(([p]) => {
      if (allOn) {
        // turn everything OFF: revoke role perms, drop extra grants
        arr.delete(p);
        if (inRole.has(p)) arr.add("-" + p);
      } else {
        // turn everything ON: clear revokes, grant non-role perms
        arr.delete("-" + p);
        if (!inRole.has(p)) arr.add(p);
      }
    });
    setEditing({ ...editing, permissions: [...arr] });
  };
  const toggleStore = (id) => {
    const arr = new Set(editing.store_ids || []);
    if (arr.has(id)) arr.delete(id); else arr.add(id);
    setEditing({ ...editing, store_ids: [...arr] });
  };

  return (
    <Shell>
      <PageHeader
        title="Users"
        subtitle="Create accounts, assign roles, stores and custom permissions."
        testId="users-header"
        actions={<button onClick={() => setEditing({ email: "", password: "", name: "", role: "cashier", store_ids: [], permissions: [] })} data-testid="add-user-btn" className="inline-flex items-center gap-2 h-10 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md"><Plus size={16} weight="bold" />New User</button>}
      />
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Stores</th>
                <th className="text-left px-4 py-3 font-semibold">Permissions</th>
                <th className="text-left px-4 py-3 font-semibold">Last Login (IST)</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold">
                    <span className="inline-flex items-center gap-2">
                      <span
                        title={isOnline(u) ? "Online (active in the last 5 min)" : "Offline"}
                        data-testid={`user-online-${u.email}`}
                        className={`inline-block w-2 h-2 rounded-full ${isOnline(u) ? "bg-emerald-500" : "bg-slate-300"}`}
                      />
                      {u.name}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono-num text-slate-600">{u.email}</td>
                  <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 bg-slate-100 rounded-full text-[10px] uppercase tracking-widest font-semibold">{(u.role || "").replace("_"," ")}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(u.store_ids || []).length || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {u.role === "super_admin" || u.permissions?.includes("*")
                      ? "Full"
                      : (u.permissions?.length || 0) > 0
                      ? `${u.effective_permissions?.length ?? "—"} effective · ${u.permissions.length} custom`
                      : "Role default"}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono-num text-slate-600" data-testid={`user-last-login-${u.email}`}>
                    {isOnline(u) ? <span className="text-emerald-700 font-bold">Online now</span> : (fmtIST(u.last_login_at) || "Never")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing({ ...u, password: "" })} data-testid={`edit-user-${u.email}`} className="p-1.5 text-slate-600 hover:text-slate-900 mr-1"><PencilSimple size={15} /></button>
                    <button onClick={() => del(u)} data-testid={`del-user-${u.email}`} className="p-1.5 text-red-600 hover:text-red-700"><Trash size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
              <div className="font-display font-bold text-lg">{editing.id ? "Edit User" : "New User"}</div>
              <button onClick={() => setEditing(null)} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Name</label>
                  <input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="user-name" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Role</label>
                  <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="user-role" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm">
                    {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <div className="text-[10px] text-slate-500 mt-1">{(ROLES.find((r) => r.value === editing.role) || {}).desc}</div>
                </div>
                {!editing.id && (
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Email</label>
                    <input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="user-email" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">{editing.id ? "New password (blank to keep)" : "Password"}</label>
                  <input type="password" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} data-testid="user-password" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm" />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2 block">Store Access</label>
                <div className="flex flex-wrap gap-2">
                  {stores.map((s) => {
                    const on = (editing.store_ids || []).includes(s.id);
                    return (
                      <button key={s.id} onClick={() => toggleStore(s.id)} className={`px-3 h-8 text-xs font-semibold rounded-full border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700"}`}>
                        {s.name}
                      </button>
                    );
                  })}
                  {stores.length === 0 && <span className="text-xs text-slate-400">No stores yet.</span>}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 block">Permissions</label>
                  {editing.role !== "super_admin" && (
                    <button
                      onClick={() => {
                        const has = (editing.permissions || []).includes("*");
                        setEditing({ ...editing, permissions: has ? [] : ["*"] });
                      }}
                      data-testid="perm-toggle-all"
                      className={`px-3 h-7 text-[11px] font-bold rounded-full border transition-colors ${(editing.permissions || []).includes("*") ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600"}`}
                    >
                      {(editing.permissions || []).includes("*") ? "Full Access (*) — ON" : "Grant Full Access (*)"}
                    </button>
                  )}
                </div>
                {editing.role === "super_admin" ? (
                  <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">Super Admin always has every permission.</div>
                ) : (editing.permissions || []).includes("*") ? (
                  <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">Wildcard granted — this user has every permission regardless of role.</div>
                ) : (
                  <div className="space-y-3" data-testid="perm-groups">
                    <div className="text-[11px] text-slate-500">Every tile is clickable — green = allowed. Click to grant or revoke any permission for this user (role defaults can be revoked too).</div>
                    {PERM_GROUPS.map(({ group, perms }) => {
                      const inRole = new Set(roleDefaults[editing.role] || []);
                      const extras = new Set(editing.permissions || []);
                      const isOn = (p) => (inRole.has(p) && !extras.has("-" + p)) || extras.has(p);
                      const allOn = perms.every(([p]) => isOn(p));
                      return (
                        <div key={group} className="border border-slate-200 rounded-md p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">{group}</div>
                            <button onClick={() => toggleGroup(perms, allOn)} data-testid={`perm-group-all-${group.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-900">
                              {allOn ? "Clear all" : "Select all"}
                            </button>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {perms.map(([p, label]) => {
                              const viaRole = inRole.has(p);
                              const revoked = extras.has("-" + p);
                              const on = isOn(p);
                              const sub = viaRole ? (revoked ? "revoked" : "via role") : (extras.has(p) ? "custom" : p);
                              return (
                                <button
                                  key={p}
                                  onClick={() => togglePerm(p, viaRole)}
                                  data-testid={`perm-${p}`}
                                  title={p}
                                  className={`px-2.5 h-9 text-[11px] font-semibold rounded-md border text-left transition-colors ${
                                    on
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-400 hover:border-emerald-500"
                                      : revoked
                                      ? "bg-red-50 text-red-600 border-red-300 hover:border-red-400"
                                      : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"
                                  }`}
                                >
                                  <span className="block truncate">{label}</span>
                                  <span className="block text-[9px] font-mono-num opacity-70 truncate">{sub}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={() => setEditing(null)} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
              <button onClick={save} data-testid="save-user-btn" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md">Save</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
